import { useEffect, useRef, useState } from 'react';
import { Music2, Volume2, VolumeX } from 'lucide-react';

const ENABLED_KEY = 'jonnys-seasonal-audio-enabled';
const VOLUME_KEY = 'jonnys-seasonal-audio-volume';
const DEFAULT_VOLUME = 0.18;
const MAX_VOLUME = 0.4;

function readBoolean(key, fallback = false) {
  try {
    const value = window.localStorage.getItem(key);
    return value === null ? fallback : value === 'true';
  } catch {
    return fallback;
  }
}

function readVolume() {
  try {
    const stored = window.localStorage.getItem(VOLUME_KEY);
    if (stored === null) return DEFAULT_VOLUME;
    const value = Number(stored);
    return Number.isFinite(value) && value >= 0 && value <= MAX_VOLUME ? value : DEFAULT_VOLUME;
  } catch {
    return DEFAULT_VOLUME;
  }
}

export default function SeasonalAudio({ music, themeLabel }) {
  const musicSrc = music?.src;
  const musicTitle = music?.title ?? 'Seasonal soundtrack';
  const musicAccent = music?.accent ?? '#ff2b87';
  const audioRef = useRef(null);
  const [enabled, setEnabled] = useState(() => readBoolean(ENABLED_KEY));
  const [volume, setVolume] = useState(readVolume);
  const enabledRef = useRef(enabled);
  const volumeRef = useRef(volume);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackBlocked, setPlaybackBlocked] = useState(false);

  useEffect(() => {
    if (!musicSrc) return undefined;

    const audio = new Audio(musicSrc);
    audio.loop = true;
    audio.preload = 'metadata';
    audio.volume = volumeRef.current;
    audioRef.current = audio;

    const handlePlay = () => {
      setIsPlaying(true);
      setPlaybackBlocked(false);
    };
    const handlePause = () => setIsPlaying(false);
    const handleError = () => {
      setIsPlaying(false);
      setPlaybackBlocked(true);
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('error', handleError);

    if (enabledRef.current) {
      audio.play().catch(() => {
        setPlaybackBlocked(true);
        setIsPlaying(false);
      });
    }

    return () => {
      audio.pause();
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('error', handleError);
      audio.removeAttribute('src');
      audio.load();
      if (audioRef.current === audio) audioRef.current = null;
    };
  }, [musicSrc]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    volumeRef.current = volume;
    if (audioRef.current) audioRef.current.volume = volume;
    try {
      window.localStorage.setItem(VOLUME_KEY, String(volume));
    } catch {
      // Preference storage can be unavailable in privacy-restricted browsers.
    }
  }, [volume]);

  useEffect(() => {
    const handleVisibility = () => {
      const audio = audioRef.current;
      if (!audio) return;

      if (document.hidden) {
        audio.pause();
        return;
      }

      if (enabled) {
        audio.play().catch(() => setPlaybackBlocked(true));
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [enabled]);

  if (!musicSrc) return null;

  async function toggleSound() {
    const audio = audioRef.current;
    if (!audio) return;

    if (enabled && isPlaying) {
      audio.pause();
      setEnabled(false);
      try {
        window.localStorage.setItem(ENABLED_KEY, 'false');
      } catch {
        // Ignore storage restrictions.
      }
      return;
    }

    try {
      audio.volume = volumeRef.current;
      await audio.play();
      setEnabled(true);
      setPlaybackBlocked(false);
      try {
        window.localStorage.setItem(ENABLED_KEY, 'true');
      } catch {
        // Ignore storage restrictions.
      }
    } catch {
      setPlaybackBlocked(true);
      setIsPlaying(false);
    }
  }

  function changeVolume(event) {
    const nextVolume = Math.min(MAX_VOLUME, Math.max(0, Number(event.target.value)));
    setVolume(nextVolume);
  }

  const statusText = playbackBlocked
    ? 'Tap to play seasonal sound'
    : isPlaying
      ? 'Seasonal sound on'
      : 'Play seasonal sound';

  return (
    <aside
      className={`seasonal-audio ${isPlaying ? 'seasonal-audio--playing' : ''}`}
      aria-label={`${themeLabel} seasonal soundtrack`}
      style={{ '--season-audio-accent': musicAccent }}
    >
      <button
        className="seasonal-audio__toggle"
        type="button"
        onClick={toggleSound}
        aria-pressed={isPlaying}
        title={`${statusText}: ${musicTitle}`}
      >
        <span className="seasonal-audio__icon" aria-hidden="true">
          {isPlaying ? <Volume2 size={18} /> : <VolumeX size={18} />}
        </span>
        <span className="seasonal-audio__copy">
          <strong>{statusText}</strong>
          <small><Music2 size={12} /> {musicTitle}</small>
        </span>
      </button>

      {isPlaying && (
        <label className="seasonal-audio__volume" title="Seasonal soundtrack volume">
          <span className="sr-only">Seasonal soundtrack volume</span>
          <Volume2 size={15} aria-hidden="true" />
          <input
            type="range"
            min="0"
            max={MAX_VOLUME}
            step="0.01"
            value={volume}
            onChange={changeVolume}
          />
        </label>
      )}
    </aside>
  );
}
